(function initComparisonProductDrawer() {
  if (customElements.get('comparison-product-drawer')) return;

  const DrawerBase = customElements.get('x-drawer');
  if (!DrawerBase) {
    if (customElements.whenDefined) {
      customElements.whenDefined('x-drawer').then(initComparisonProductDrawer);
    }
    return;
  }

  class ComparisonProductDrawer extends DrawerBase {
    get openFrom() {
      return window.matchMedia(`${window.themeVariables.breakpoints['sm-max']}`).matches ? 'top' : this.getAttribute('open-from') || 'right';
    }

    show(animate) {
      if (this.open) {
        return Promise.resolve();
      }

      document.querySelectorAll('comparison-product-drawer[data-comparison-drawer][open]').forEach((drawer) => {
        if (drawer !== this && typeof drawer.hide === 'function') {
          drawer.hide();
        }
      });

      return super.show(animate);
    }
  }

  customElements.define('comparison-product-drawer', ComparisonProductDrawer);
})();

if (!customElements.get('bk-media-product-comparison-grid')) {
  class BkMediaProductComparisonGrid extends HTMLElement {
    connectedCallback() {
      this.productsData = this.loadProductsData();
      this.activeBlockId = null;
      this.drawerSelectedIds = [];
      this.activeColumnCount = null;

      this.onPickerClick = this.onPickerClick.bind(this);
      this.onDrawerOptionClick = this.onDrawerOptionClick.bind(this);
      this.onDrawerFooterClick = this.onDrawerFooterClick.bind(this);
      this.onDrawerSearch = this.onDrawerSearch.bind(this);
      this.onDrawerHide = this.onDrawerHide.bind(this);
      this.onDrawerShow = this.onDrawerShow.bind(this);
      this.onCriteriaToggle = this.onCriteriaToggle.bind(this);
      this.onGalleryTriggerClick = this.onGalleryTriggerClick.bind(this);
      this.onColumnControlClick = this.onColumnControlClick.bind(this);
      this.syncRowHeights = this.syncRowHeights.bind(this);

      this.initDrawer();
      this.bundleButtons = this.querySelectorAll('[data-comparison-bundle-action]');
      this.bundleSavingsRows = this.querySelectorAll('[data-comparison-bundle-savings]');
      this.bundleSavingsAmountDesktop = this.querySelector('[data-comparison-bundle-savings-amount-desktop]');
      this.bundleSavingsAmountMobile = this.querySelector('[data-comparison-bundle-savings-amount-mobile]');

      this.initCriteriaFilter();
      this.initScrollSync();
      this.applyPreselectedProducts();
      this.setActiveColumnCount(this.getInitialColumnCount());
      this.updateBundleButton();
      this.syncRowHeights();

      document.addEventListener('click', this.onPickerClick, true);
      document.addEventListener('click', this.onDrawerOptionClick);
      document.addEventListener('click', this.onDrawerFooterClick);
      this.addEventListener('click', this.onColumnControlClick);
      this.addEventListener('click', this.onGalleryTriggerClick);
      document.addEventListener('input', this.onDrawerSearch);
      document.addEventListener('reset', this.onDrawerSearch, true);

      this.onResize = () => requestAnimationFrame(() => {
        this.setActiveColumnCount(this.getActiveColumnCount());
        this.syncStickyPickerHeight();
        this.syncRowHeights();
        this.updateScrollability();
      });
      window.addEventListener('resize', this.onResize);

      this.resizeObserver = new ResizeObserver(() => {
        requestAnimationFrame(() => {
          this.syncStickyPickerHeight();
          this.syncRowHeights();
        });
      });
      this.resizeObserver.observe(this);

      this.querySelectorAll('img').forEach((img) => {
        if (!img.complete) {
          img.addEventListener('load', this.syncRowHeights, { once: true });
        }
      });

      document.addEventListener('shopify:section:load', this.onSectionLoad);
      this.bundleButtons.forEach((button) => {
        button.addEventListener('click', (event) => this.onBundleClick(event));
      });
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onPickerClick, true);
      document.removeEventListener('click', this.onDrawerOptionClick);
      document.removeEventListener('click', this.onDrawerFooterClick);
      this.removeEventListener('click', this.onColumnControlClick);
      this.removeEventListener('click', this.onGalleryTriggerClick);
      document.removeEventListener('input', this.onDrawerSearch);
      document.removeEventListener('reset', this.onDrawerSearch, true);
      this.unbindDrawerEvents();
      this.unbindScrollSync();
      window.removeEventListener('resize', this.onResize);
      this.resizeObserver?.disconnect();
      document.removeEventListener('shopify:section:load', this.onSectionLoad);
      if (this.swapConfirmationTimeout) clearTimeout(this.swapConfirmationTimeout);
    }

    initScrollSync() {
      this.unbindScrollSync();
      this.scrollAreas = [...this.querySelectorAll('[data-comparison-grid-scroll]')];
      if (this.scrollAreas.length < 2) return;

      this.scrollSyncing = false;
      this.onSyncedScroll = (event) => {
        if (this.scrollSyncing) return;
        this.scrollSyncing = true;
        const left = event.currentTarget.scrollLeft;
        this.scrollAreas.forEach((area) => {
          if (area !== event.currentTarget) area.scrollLeft = left;
        });
        requestAnimationFrame(() => {
          this.scrollSyncing = false;
        });
      };

      this.scrollAreas.forEach((area) => {
        area.addEventListener('scroll', this.onSyncedScroll, { passive: true });
      });

      this.syncStickyPickerHeight();
    }

    unbindScrollSync() {
      if (this.scrollAreas && this.onSyncedScroll) {
        this.scrollAreas.forEach((area) => {
          area.removeEventListener('scroll', this.onSyncedScroll);
        });
      }
      this.scrollAreas = [];
      this.onSyncedScroll = null;
    }

    syncStickyPickerHeight() {
      const pickerSticky = this.querySelector('[data-comparison-grid-sticky-picker]');
      const content = this.querySelector('.bk-media-comparison-grid__content');
      if (!pickerSticky || !content) return;

      const height = pickerSticky.getBoundingClientRect().height;
      if (height > 0) {
        content.style.setProperty('--bk-comparison-sticky-picker-height', `${height}px`);
      }
    }

    onSectionLoad = (event) => {
      if (!event.target?.contains(this)) return;
      this.productsData = this.loadProductsData();
      this.initDrawer();
      this.initCriteriaFilter();
      this.initScrollSync();
      this.applyPreselectedProducts();
      this.setActiveColumnCount(this.getInitialColumnCount());
      this.updateBundleButton();
      this.updateScrollability();
      requestAnimationFrame(() => {
        this.syncStickyPickerHeight();
        this.syncRowHeights();
        this.updateScrollability();
      });
    };

    initDrawer() {
      this.unbindDrawerEvents();
      this.drawer = this.querySelector(`comparison-product-drawer[data-comparison-drawer][data-comparison-section-id="${this.dataset.sectionId}"]`);

      if (this.drawer) {
        this.drawer.addEventListener('dialog:after-hide', this.onDrawerHide);
        this.drawer.addEventListener('dialog:after-show', this.onDrawerShow);
      }
    }

    unbindDrawerEvents() {
      this.drawer?.removeEventListener('dialog:after-hide', this.onDrawerHide);
      this.drawer?.removeEventListener('dialog:after-show', this.onDrawerShow);
    }

    hasSelectors() {
      return this.dataset.comparisonHasSelectors === 'true';
    }

    loadProductsData() {
      const script = this.querySelector('[data-comparison-products]');
      if (!script) return {};

      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        console.error('Failed to parse comparison grid products JSON:', error);
        return {};
      }
    }

    getColumnHeaders() {
      return [...this.querySelectorAll('[data-comparison-grid-header]')];
    }

    getMinSelectable() {
      const min = parseInt(this.dataset.comparisonMinProducts, 10);
      return Number.isFinite(min) && min > 0 ? min : 2;
    }

    isMobileViewport() {
      return window.matchMedia('(max-width: 999px)').matches;
    }

    getMaxSelectable() {
      const desktopMax = parseInt(this.dataset.comparisonMaxProducts, 10);
      const mobileMax = parseInt(this.dataset.comparisonMaxProductsMobile, 10);
      const headerCount = this.getColumnHeaders().length;
      const configuredMax = this.isMobileViewport()
        ? (Number.isFinite(mobileMax) && mobileMax > 0 ? mobileMax : 2)
        : (Number.isFinite(desktopMax) && desktopMax > 0 ? desktopMax : 3);

      return Math.min(configuredMax, headerCount);
    }

    getActiveColumnCount() {
      return this.activeColumnCount ?? this.getColumnHeaders().length;
    }

    getActiveColumnHeaders() {
      return this.getColumnHeaders().slice(0, this.getActiveColumnCount());
    }

    getDisplayMin() {
      return this.hasSelectors() ? 1 : this.getMinSelectable();
    }

    getPreselectedProductIds() {
      const raw = this.dataset.comparisonPreselectedIds || '';
      return raw.split(',').map((id) => id.trim()).filter(Boolean);
    }

    applyPreselectedProducts() {
      const ids = this.getPreselectedProductIds();
      if (!ids.length) return;

      const headers = this.getColumnHeaders();
      ids.slice(0, this.getMaxSelectable()).forEach((productId, index) => {
        const header = headers[index];
        if (header?.dataset.blockId && productId) {
          this.switchColumnProduct(header.dataset.blockId, productId, { silent: true });
        }
      });
    }

    getInitialColumnCount() {
      const max = this.getMaxSelectable();
      const min = this.getDisplayMin();
      const preselected = this.getPreselectedProductIds();
      if (preselected.length > 0) {
        return Math.min(Math.max(preselected.length, min), max);
      }

      const initial = parseInt(this.dataset.comparisonInitialColumns, 10);
      const configured = Number.isFinite(initial) && initial > 0
        ? Math.min(Math.max(initial, min), max)
        : max;
      return Math.min(configured, max);
    }

    hasSelectors() {
      return this.dataset.comparisonHasSelectors === 'true';
    }

    getSelectedProductIds() {
      return this.getActiveColumnHeaders()
        .map((header) => header.dataset.productId)
        .filter(Boolean)
        .map(String);
    }

    getPlaceholderCount(productCount = this.getActiveColumnCount()) {
      if (!this.hasSelectors()) return 0;

      const remaining = this.getMaxSelectable() - productCount;
      if (remaining <= 0) return 0;

      const desired = this.isMobileViewport() ? 1 : 2;
      return Math.min(desired, remaining);
    }

    getDisplayColumnCount() {
      return this.getActiveColumnCount() + this.getPlaceholderCount();
    }

    setActiveColumnCount(count) {
      const max = this.getMaxSelectable();
      const min = Math.min(this.getDisplayMin(), max);
      const nextCount = Math.max(min, Math.min(count, max));
      this.activeColumnCount = nextCount;

      const headers = this.getColumnHeaders();
      const placeholderCount = this.getPlaceholderCount(nextCount);

      headers.forEach((header, index) => {
        const blockId = header.dataset.blockId;
        const isProduct = index < nextCount;
        const isAdd = index >= nextCount && index < nextCount + placeholderCount;

        this.querySelectorAll(`[data-block-id="${blockId}"]`).forEach((element) => {
          if (!element.matches('[data-comparison-grid-header], [data-comparison-grid-image], .bk-media-comparison-grid__spec-cell')) return;
          element.hidden = !(isProduct || isAdd);
          element.classList.toggle('is-add-placeholder', isAdd);
        });
      });

      this.style.setProperty('--bk-comparison-grid-column-count', String(Math.max(this.getDisplayColumnCount(), 1)));
      this.dataset.comparisonActiveColumns = String(nextCount);

      this.updateColumnControls();
      this.updateBundleButton();
      this.updateDrawerOptionStates();
      this.updateScrollability();
      requestAnimationFrame(() => {
        this.syncStickyPickerHeight();
        this.syncRowHeights();
        this.updateScrollability();
      });
    }

    getVisibleColumnCount() {
      const isMobile = this.isMobileViewport();
      const desktopVisible = parseInt(getComputedStyle(this).getPropertyValue('--bk-comparison-grid-desktop-columns'), 10);
      const mobileVisible = parseInt(getComputedStyle(this).getPropertyValue('--bk-comparison-grid-mobile-columns'), 10);
      const fallback = isMobile ? 2 : 3;

      if (isMobile) {
        return Number.isFinite(mobileVisible) && mobileVisible > 0 ? mobileVisible : fallback;
      }

      return Number.isFinite(desktopVisible) && desktopVisible > 0 ? desktopVisible : fallback;
    }

    updateScrollability() {
      const primaryScroll = this.querySelector('[data-comparison-grid-scroll-primary]');
      if (!primaryScroll) return;

      const activeCount = this.getDisplayColumnCount();
      const visibleCount = this.getVisibleColumnCount();
      const isScrollable = activeCount > visibleCount;
      const scrollProgress = this.querySelector('.bk-media-comparison-grid__scrollable .scrollbar__progress');

      primaryScroll.classList.toggle('is-scrollable', isScrollable);
      this.toggleAttribute('data-comparison-scrollable', isScrollable);

      if (scrollProgress && activeCount > 0) {
        scrollProgress.style.setProperty('--scroll-progress', String(Math.min(1, visibleCount / activeCount)));
      }
    }

    updateColumnControls() {
      const count = this.getActiveColumnCount();
      const canRemove = count > this.getDisplayMin();

      this.querySelectorAll('[data-comparison-column-remove]').forEach((button) => {
        const blockId = button.dataset.blockId;
        const index = this.getColumnHeaders().findIndex((header) => header.dataset.blockId === blockId);
        button.hidden = !(canRemove && index >= 0 && index < count);
      });
    }

    onColumnControlClick(event) {
      const removeButton = event.target.closest('[data-comparison-column-remove]');
      if (!removeButton || !this.contains(removeButton)) return;

      event.preventDefault();
      event.stopPropagation();
      this.removeComparisonColumn(removeButton.dataset.blockId);
    }

    removeComparisonColumn(blockId) {
      if (!blockId) return;

      const headers = this.getColumnHeaders();
      const count = this.getActiveColumnCount();
      const removeIndex = headers.findIndex((header) => header.dataset.blockId === blockId);

      if (removeIndex < 0 || removeIndex >= count || count <= this.getDisplayMin()) return;

      const activeIds = headers.slice(0, count).map((header) => header.dataset.productId);
      const [removedId] = activeIds.splice(removeIndex, 1);

      activeIds.forEach((productId, index) => {
        if (productId) {
          this.switchColumnProduct(headers[index].dataset.blockId, productId, { silent: true });
        }
      });

      if (headers[activeIds.length] && removedId) {
        this.switchColumnProduct(headers[activeIds.length].dataset.blockId, removedId, { silent: true });
      }

      this.setActiveColumnCount(activeIds.length);
      this.showSwapConfirmation('', 'Produkt entfernt');
    }

    onPickerClick(event) {
      const trigger = event.target.closest('[data-comparison-grid-picker], [data-comparison-drawer-trigger]');
      if (!trigger || !this.contains(trigger)) return;

      const drawer = document.getElementById(trigger.dataset.drawerId);
      if (!drawer || drawer.dataset.comparisonSectionId !== this.dataset.sectionId) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      this.activeBlockId = trigger.dataset.blockId || null;
      this.initDrawerSelection();

      if (drawer.open) {
        drawer.hide();
        return;
      }

      document.querySelectorAll('comparison-product-drawer[data-comparison-drawer][open]').forEach((openDrawer) => {
        if (openDrawer !== drawer && typeof openDrawer.hide === 'function') {
          openDrawer.hide();
        }
      });

      drawer.show();
    }

    onDrawerShow(event) {
      const drawer = event?.currentTarget;
      if (drawer && drawer.dataset.comparisonSectionId !== this.dataset.sectionId) return;

      this.initDrawerSelection();
      this.updatePickerExpandedStates(true);
    }

    onDrawerHide(event) {
      const drawer = event?.currentTarget;
      if (drawer && drawer.dataset.comparisonSectionId !== this.dataset.sectionId) return;

      this.activeBlockId = null;
      this.resetDrawerSearch();
      this.updatePickerExpandedStates(false);
    }

    updatePickerExpandedStates(isOpen) {
      this.querySelectorAll('[data-comparison-grid-picker], [data-comparison-drawer-trigger]').forEach((trigger) => {
        const isActive = isOpen && trigger.dataset.blockId === this.activeBlockId;
        trigger.setAttribute('aria-expanded', isActive ? 'true' : 'false');
      });
    }

    initDrawerSelection() {
      this.drawerSelectedIds = this.getSelectedProductIds();
      this.updateDrawerOptionStates();
      this.updateDrawerFooter();
    }

    onDrawerOptionClick(event) {
      const drawer = event.target.closest('[data-comparison-drawer]');
      if (!drawer || drawer.dataset.comparisonSectionId !== this.dataset.sectionId) return;

      const removeButton = event.target.closest('[data-comparison-drawer-option-remove]');
      if (removeButton) {
        const option = removeButton.closest('.bk-media-product-comparison__drawer-option');
        if (!option) return;

        event.preventDefault();
        event.stopPropagation();
        this.removeDrawerProduct(option.dataset.productId);
        return;
      }

      const option = event.target.closest('.bk-media-product-comparison__drawer-option');
      if (!option || option.classList.contains('is-disabled')) return;

      event.preventDefault();
      event.stopPropagation();
      this.toggleDrawerProduct(option.dataset.productId);
    }

    toggleDrawerProduct(productId) {
      if (!productId) return;

      const normalizedId = String(productId);
      const currentIndex = this.drawerSelectedIds.indexOf(normalizedId);

      if (currentIndex >= 0) {
        this.drawerSelectedIds.splice(currentIndex, 1);
      } else if (this.drawerSelectedIds.length < this.getMaxSelectable()) {
        this.drawerSelectedIds.push(normalizedId);
      }

      this.updateDrawerOptionStates();
      this.updateDrawerFooter();
    }

    removeDrawerProduct(productId) {
      if (!productId) return;

      const normalizedId = String(productId);
      const currentIndex = this.drawerSelectedIds.indexOf(normalizedId);
      if (currentIndex < 0) return;

      this.drawerSelectedIds.splice(currentIndex, 1);
      this.updateDrawerOptionStates();
      this.updateDrawerFooter();
    }

    getDrawerCompareButtonText(count) {
      const remaining = this.getMinSelectable() - count;

      if (remaining > 0) {
        if (remaining === 1) return 'Noch 1 Produkt wählen';
        return `Noch ${remaining} Produkte wählen`;
      }

      return `Jetzt ${count} Produkte vergleichen`;
    }

    updateDrawerFooter() {
      if (!this.drawer) return;

      const footer = this.drawer.querySelector('[data-comparison-drawer-footer]');
      const selection = this.drawer.querySelector('[data-comparison-drawer-selection]');
      const compareButton = this.drawer.querySelector('[data-comparison-drawer-compare]');
      if (!footer || !selection || !compareButton) return;

      const selectedCount = this.drawerSelectedIds.length;
      footer.hidden = false;
      compareButton.disabled = selectedCount < this.getMinSelectable();
      compareButton.textContent = this.getDrawerCompareButtonText(selectedCount);

      selection.innerHTML = this.drawerSelectedIds.map((productId) => {
        const data = this.productsData[String(productId)] || this.productsData[productId];
        if (!data) return '';

        const title = this.escapeHtml(data.title || '');
        const imageUrl = data.imageUrl ? this.escapeHtml(data.imageUrl) : '';
        const imageAlt = this.escapeHtml(data.imageAlt || data.title || '');

        return `
          <div class="bk-media-product-comparison__drawer-chip">
            ${imageUrl ? `<img src="${imageUrl}" alt="${imageAlt}" loading="lazy" width="48" height="48">` : ''}
            <span class="bk-media-product-comparison__drawer-chip-title text-xs">${title}</span>
            <button type="button" class="bk-media-product-comparison__drawer-chip-remove" data-comparison-drawer-remove="${productId}" aria-label="Entfernen">
              <svg role="presentation" focusable="false" width="12" height="12" viewBox="0 0 16 16"><path d="M4 4L12 12M12 4L4 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
            </button>
          </div>
        `;
      }).join('');
    }

    applyDrawerComparison() {
      if (!this.drawer || this.drawerSelectedIds.length < this.getMinSelectable()) return;

      const headers = this.getColumnHeaders();
      const activeCount = Math.min(this.drawerSelectedIds.length, this.getMaxSelectable());

      this.drawerSelectedIds.slice(0, activeCount).forEach((productId, index) => {
        const header = headers[index];
        if (header?.dataset.blockId && productId) {
          this.switchColumnProduct(header.dataset.blockId, productId, { silent: true });
        }
      });

      this.setActiveColumnCount(activeCount);
      this.showSwapConfirmation('', 'Vergleich aktualisiert');

      if (this.drawer.open && typeof this.drawer.hide === 'function') {
        this.drawer.hide();
      }
    }

    onDrawerFooterClick(event) {
      const drawer = event.target.closest('[data-comparison-drawer]');
      if (!drawer || drawer.dataset.comparisonSectionId !== this.dataset.sectionId) return;

      const removeButton = event.target.closest('[data-comparison-drawer-remove]');
      if (removeButton) {
        event.preventDefault();
        this.removeDrawerProduct(removeButton.dataset.comparisonDrawerRemove);
        return;
      }

      const compareButton = event.target.closest('[data-comparison-drawer-compare]');
      if (!compareButton) return;

      event.preventDefault();
      this.applyDrawerComparison();
    }

    switchColumnProduct(blockId, productId, options = {}) {
      const data = this.productsData[String(productId)] || this.productsData[productId];
      if (!data || !blockId) return;

      this.querySelectorAll(`[data-block-id="${blockId}"]`).forEach((element) => {
        element.dataset.productId = productId;
      });

      const header = this.querySelector(`[data-comparison-grid-header][data-block-id="${blockId}"]`);
      if (header) {
        this.updateColumnHeader(header, data);
      }

      this.querySelectorAll(`.bk-media-comparison-grid__spec-cell[data-block-id="${blockId}"]`).forEach((cell) => {
        this.updateSpecCell(cell, data);
      });

      this.updateBundleButton();
      if (!options.silent) {
        this.showSwapConfirmation(data.title);
      }
      requestAnimationFrame(() => this.syncRowHeights());
    }

    updateColumnHeader(header, data) {
      const pickerLabel = header.querySelector('[data-comparison-grid-picker-label]');
      const title = header.querySelector('.bk-media-comparison-grid__column-title');
      const blockId = header?.dataset?.blockId;
      const entscheidungslabel = blockId
        ? this.querySelector(`.bk-media-comparison-grid__spec-cell[data-row="price"][data-block-id="${blockId}"] [data-comparison-entscheidungslabel]`)
        : null;

      if (pickerLabel) pickerLabel.textContent = data.title;
      if (title) title.textContent = data.title;

      const pickerTrigger = header.querySelector('[data-comparison-grid-picker]');
      if (pickerTrigger && data.title) {
        pickerTrigger.setAttribute('aria-label', `Produkt ändern: ${data.title}`);
      }
      if (entscheidungslabel) {
        const label = data.entscheidungslabel || '';
        entscheidungslabel.textContent = label;
        entscheidungslabel.hidden = !label;
      }

      this.updateColumnImage(header, data);
    }

    updateAtcButton(buttonWrap, data) {
      if (!buttonWrap) return;

      const form = buttonWrap.querySelector('form');
      const input = buttonWrap.querySelector('[data-comparison-variant-input]');
      const submitButton = buttonWrap.querySelector('.button');

      if (form && data.handle) {
        form.action = `${window.Shopify?.routes?.root || '/'}products/${data.handle}`;
      }

      if (input) input.value = data.variantId || '';

      if (submitButton) {
        submitButton.disabled = data.buttonDisabled;
        submitButton.toggleAttribute('disabled', data.buttonDisabled);

        const labelEl = submitButton.querySelector('.bk-media-comparison-grid__atc-label');
        if (labelEl) {
          labelEl.textContent = data.buttonText;
        } else {
          const textWrapper = submitButton.querySelector('.text-with-icon') || submitButton;
          if (textWrapper.childNodes.length) {
            const textNode = [...textWrapper.childNodes].find((node) => node.nodeType === Node.TEXT_NODE);
            if (textNode) {
              textNode.textContent = data.buttonText;
            } else if (!submitButton.querySelector('.sr-only')) {
              textWrapper.textContent = data.buttonText;
            }
          }
        }
      }
    }

    updateColumnImage(header, data) {
      const blockId = header?.dataset?.blockId;
      const imageContainer = (blockId && this.querySelector(`[data-comparison-grid-image][data-block-id="${blockId}"]`))
        || header?.querySelector?.('.bk-media-comparison-grid__column-image');
      if (!imageContainer) return;

      const hasGallery = Array.isArray(data.gallery) && data.gallery.length > 0;
      let trigger = imageContainer.querySelector('[data-comparison-gallery-trigger]');

      if (!trigger) {
        trigger = document.createElement('button');
        trigger.type = 'button';
        trigger.className = 'bk-media-product-comparison__image-trigger';
        trigger.setAttribute('data-comparison-gallery-trigger', '');

        const imageWrap = document.createElement('span');
        imageWrap.className = 'bk-media-product-comparison__image';
        trigger.appendChild(imageWrap);
        imageContainer.appendChild(trigger);
      }

      trigger.dataset.productId = String(data.id);
      trigger.disabled = !hasGallery;
      trigger.setAttribute('aria-disabled', hasGallery ? 'false' : 'true');

      const imageWrap = trigger.querySelector('.bk-media-product-comparison__image');

      if (data.imageUrl && imageWrap) {
        trigger.hidden = false;
        imageWrap.innerHTML = `<img src="${this.escapeHtml(data.imageUrl)}" alt="${this.escapeHtml(data.imageAlt || data.title)}" loading="lazy" width="400" height="400">`;

        if (hasGallery) {
          imageWrap.insertAdjacentHTML('beforeend', `<span class="bk-media-product-comparison__image-zoom" aria-hidden="true"><svg role="presentation" fill="none" stroke-width="2" focusable="false" width="12" height="12" viewBox="0 0 15 15"><circle cx="7.067" cy="7.067" r="6.067" stroke="currentColor"></circle><path d="M11.4 11.4 14 14" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"></path><path d="M7 4v6M4 7h6" stroke="currentColor"></path></svg></span>`);
        }
      } else {
        trigger.hidden = true;
        if (imageWrap) imageWrap.innerHTML = '';
      }
    }

    updateSpecCell(cell, data) {
      const rowKey = cell.dataset.row;
      if (!rowKey) return;

      if (rowKey === 'price') {
        this.updatePriceCell(cell, data);
        return;
      }

      if (rowKey === 'atc') {
        this.updateAtcButton(cell.querySelector('[data-comparison-button]'), data);
        const productLink = cell.querySelector('[data-comparison-grid-product-link-wrap] a, [data-comparison-grid-product-link-wrap] .button');
        if (productLink && data.handle) {
          productLink.href = `${window.Shopify?.routes?.root || '/'}products/${data.handle}`;
        }
        return;
      }

      if (rowKey === 'kombinierbar') {
        this.updateKombinierbarCell(cell, data);
        return;
      }

      if (rowKey === 'benefits') {
        this.updateBenefitsCell(cell, data);
        return;
      }

      const container = cell.querySelector('.bk-media-comparison-grid__cell-value');
      if (!container) return;

      const values = this.getRowValues(rowKey, data);
      const valueClass = rowKey === 'shots' ? 'h1' : 'p';
      container.innerHTML = values.map((value) => `<p class="${valueClass}">${this.escapeHtml(value)}</p>`).join('');
    }

    updateBenefitsCell(cell, data) {
      const container = cell.querySelector('.bk-media-comparison-grid__cell-value');
      if (!container) return;

      const icons = [
        'https://cdn.shopify.com/s/files/1/0549/7546/3624/files/icons8-ziel-64.png?v=1718377092',
        'https://cdn.shopify.com/s/files/1/0549/7546/3624/files/icons8-naturkost-64.png?v=1718377092',
        'https://cdn.shopify.com/s/files/1/0549/7546/3624/files/icons8-offenes-buch-64.png?v=1718377092',
      ];
      const values = [data.usp1, data.usp2, data.usp3];

      container.innerHTML = values.map((value, index) => {
        if (!value) {
          return '<div class="bk-media-comparison-grid__benefit-row"><p class="p">/</p></div>';
        }
        return `<div class="bk-media-comparison-grid__benefit-row"><img src="${icons[index]}" alt="" class="bk-media-comparison-grid__benefit-icon" width="100" height="100"><p class="p">${this.escapeHtml(value)}</p></div>`;
      }).join('');
    }

    updateKombinierbarCell(cell, data) {
      const container = cell.querySelector('.bk-media-comparison-grid__cell-value');
      if (!container) return;

      const items = Array.isArray(data.kombinierbarMit) ? data.kombinierbarMit : [];
      if (!items.length) {
        container.innerHTML = '<p class="p">/</p>';
        return;
      }

      container.innerHTML = `<div class="bk-media-comparison-grid__kombinierbar-list">${items.map((item) => {
        const image = item.imageUrl
          ? `<img src="${this.escapeHtml(item.imageUrl)}" alt="${this.escapeHtml(item.title || '')}" loading="lazy" width="96" height="96">`
          : '';
        return `
          <div class="bk-media-comparison-grid__kombinierbar-option">
            <div class="bk-media-comparison-grid__kombinierbar-option-content">
              ${image}
              <span class="bold text-sm">${this.escapeHtml(item.title || '')}</span>
            </div>
          </div>
        `;
      }).join('')}</div>`;
    }

    updatePriceCell(cell, data) {
      const container = cell.querySelector('.bk-media-comparison-grid__cell-value');
      if (!container) return;

      let ratingEl = container.querySelector('[data-comparison-grid-rating]');
      let labelEl = container.querySelector('[data-comparison-entscheidungslabel]');
      let priceEl = container.querySelector('[data-comparison-grid-price]');
      let preisproEl = container.querySelector('[data-comparison-grid-preispro]');
      const label = data.entscheidungslabel || '';
      const badgeHtml = data.judgemeBadge || '';
      const productId = this.escapeHtml(String(data.id || ''));

      if (!priceEl) {
        container.innerHTML = `
          <div class="bk-media-comparison-grid__rating" data-comparison-grid-rating${badgeHtml ? '' : ' hidden'}>
            <div class="jdgm-widget jdgm-preview-badge" data-id="${productId}" data-auto-install="false">${badgeHtml}</div>
          </div>
          <p class="bk-media-comparison-grid__entscheidungslabel h6" data-comparison-entscheidungslabel${label ? '' : ' hidden'}>${this.escapeHtml(label)}</p>
          <p class="p" data-comparison-grid-price>${this.escapeHtml(data.price || '/')}</p>
          <p class="p text-sm" data-comparison-grid-preispro>${this.escapeHtml(data.preispro || '/')}</p>
        `;
      } else {
        if (!ratingEl) {
          ratingEl = document.createElement('div');
          ratingEl.className = 'bk-media-comparison-grid__rating';
          ratingEl.setAttribute('data-comparison-grid-rating', '');
        }

        ratingEl.innerHTML = `<div class="jdgm-widget jdgm-preview-badge" data-id="${productId}" data-auto-install="false">${badgeHtml}</div>`;
        ratingEl.hidden = !badgeHtml;

        if (!preisproEl) {
          preisproEl = document.createElement('p');
          preisproEl.className = 'p text-sm';
          preisproEl.setAttribute('data-comparison-grid-preispro', '');
        }

        if (!labelEl) {
          labelEl = document.createElement('p');
          labelEl.className = 'bk-media-comparison-grid__entscheidungslabel h6';
          labelEl.setAttribute('data-comparison-entscheidungslabel', '');
        }

        priceEl.textContent = data.price || '/';
        preisproEl.textContent = data.preispro || '/';
        preisproEl.hidden = false;
        labelEl.textContent = label;
        labelEl.hidden = !label;

        container.append(ratingEl, labelEl, priceEl, preisproEl);
      }

      this.updateAtcButton(cell.querySelector('[data-comparison-button]'), data);
      this.refreshJudgemeBadges();
    }

    refreshJudgemeBadges() {
      const customize = window.jdgm?.customizeBadges;
      if (typeof customize === 'function') {
        customize.call(window.jdgm);
      }
    }

    getRowValues(rowKey, data) {
      switch (rowKey) {
        case 'price':
          return [data.price || '/', data.preispro || '/'];
        case 'shots':
          return [data.shotsCount || '/'];
        case 'benefits':
          return [data.usp1 || '/', data.usp2 || '/', data.usp3 || '/'];
        case 'tageszeit':
          return [data.tageszeit || '/'];
        case 'geschmack':
          return [data.geschmack || '/'];
        case 'ingredients':
          return [data.ingredient1 || '/', data.ingredient2 || '/', data.ingredient3 || '/', data.ingredient4 || '/'];
        default:
          return ['/'];
      }
    }

    updateDrawerOptionStates() {
      if (!this.drawer) return;

      const maxSelectable = this.getMaxSelectable();
      if (this.drawerSelectedIds.length > maxSelectable) {
        this.drawerSelectedIds = this.drawerSelectedIds.slice(0, maxSelectable);
      }

      const atMax = this.drawerSelectedIds.length >= maxSelectable;

      this.drawer.querySelectorAll('.bk-media-product-comparison__drawer-option').forEach((option) => {
        const productId = String(option.dataset.productId);
        const isSelected = this.drawerSelectedIds.includes(productId);

        option.classList.toggle('is-selected', isSelected);
        option.classList.toggle('is-disabled', atMax && !isSelected);
        option.setAttribute('aria-pressed', isSelected ? 'true' : 'false');

        const removeButton = option.querySelector('[data-comparison-drawer-option-remove]');
        if (removeButton) {
          removeButton.hidden = !isSelected;
        }
      });

      this.sortDrawerOptions();
    }

    sortDrawerOptions() {
      if (!this.drawer) return;

      const results = this.drawer.querySelector('.bk-media-product-comparison__drawer-results');
      if (!results) return;

      const options = [...results.querySelectorAll('.bk-media-product-comparison__drawer-option')];
      const selected = options.filter((option) => option.classList.contains('is-selected'));
      const others = options.filter((option) => !option.classList.contains('is-selected'));

      [...selected, ...others].forEach((option) => {
        results.appendChild(option);
      });
    }

    onDrawerSearch(event) {
      const input = event.target.closest('[data-comparison-drawer-search], [data-comparison-grid-drawer-search]');
      if (!input || !this.drawer?.contains(input)) return;

      if (event.type === 'reset') {
        requestAnimationFrame(() => this.filterDrawerOptions(''));
        return;
      }

      this.filterDrawerOptions(input.value);
    }

    filterDrawerOptions(query) {
      if (!this.drawer) return;

      const normalized = query.trim().toLowerCase();
      let visibleCount = 0;

      this.drawer.querySelectorAll('.bk-media-product-comparison__drawer-option').forEach((option) => {
        const title = (option.dataset.searchTitle || option.textContent || '').toLowerCase();
        const isVisible = normalized === '' || title.includes(normalized);
        option.hidden = !isVisible;
        if (isVisible) visibleCount += 1;
      });

      const emptyState = this.drawer.querySelector('.bk-media-product-comparison__drawer-empty, .bk-media-comparison-grid__drawer-empty');
      if (emptyState) emptyState.hidden = visibleCount > 0;

      this.sortDrawerOptions();
    }

    resetDrawerSearch() {
      if (!this.drawer) return;

      const input = this.drawer.querySelector('[data-comparison-drawer-search], [data-comparison-grid-drawer-search]');
      if (input) input.value = '';
      this.filterDrawerOptions('');
    }

    initCriteriaFilter() {
      const filter = this.querySelector('[data-comparison-criteria-filter]');
      if (!filter) return;

      filter.querySelectorAll('[data-comparison-criterion-toggle]').forEach((input) => {
        input.removeEventListener('change', this.onCriteriaToggle);
        input.addEventListener('change', this.onCriteriaToggle);
      });

      this.applyCriteriaVisibility();
    }

    onCriteriaToggle() {
      this.applyCriteriaVisibility();
    }

    applyCriteriaVisibility() {
      const filter = this.querySelector('[data-comparison-criteria-filter]');
      if (!filter) return;

      filter.querySelectorAll('[data-comparison-criterion-toggle]').forEach((toggle) => {
        const key = toggle.dataset.comparisonCriterionToggle;
        if (!key) return;

        const visible = toggle.checked;
        this.querySelectorAll(`.bk-media-comparison-grid__spec-group[data-row="${key}"]`).forEach((group) => {
          group.hidden = !visible;
        });

        if (key === 'price') {
          const stickyPrice = this.querySelector('[data-comparison-grid-sticky-price]');
          if (stickyPrice) stickyPrice.hidden = !visible;
        }
      });

      requestAnimationFrame(() => {
        this.syncStickyPickerHeight();
        this.syncRowHeights();
      });
    }

    syncRowHeights() {
      this.querySelectorAll('.bk-media-comparison-grid__spec-cell').forEach((cell) => {
        cell.style.minHeight = '';
      });
      this.querySelectorAll('.bk-media-comparison-grid__kombinierbar-option').forEach((option) => {
        option.style.minHeight = '';
      });

      const rowKeys = [...new Set([...this.querySelectorAll('.bk-media-comparison-grid__spec-group:not([hidden])')].map((group) => group.dataset.row))];

      rowKeys.forEach((key) => {
        const cells = [...this.querySelectorAll(`.bk-media-comparison-grid__spec-cell[data-row="${key}"]:not([hidden])`)];
        let maxHeight = 0;

        cells.forEach((cell) => {
          cell.style.minHeight = '';
          maxHeight = Math.max(maxHeight, cell.getBoundingClientRect().height);
        });

        cells.forEach((cell) => {
          cell.style.minHeight = `${Math.ceil(maxHeight)}px`;
        });
      });

      const kombinierbarOptions = [...this.querySelectorAll('.bk-media-comparison-grid__spec-cell[data-row="kombinierbar"]:not([hidden]) .bk-media-comparison-grid__kombinierbar-option')];
      let kombinierbarMax = 0;
      kombinierbarOptions.forEach((option) => {
        kombinierbarMax = Math.max(kombinierbarMax, option.getBoundingClientRect().height);
      });
      kombinierbarOptions.forEach((option) => {
        if (kombinierbarMax > 0) option.style.minHeight = `${Math.ceil(kombinierbarMax)}px`;
      });
    }

    showSwapConfirmation(productTitle, customMessage) {
      if (!this.swapConfirmation) {
        this.swapConfirmation = this.querySelector('.bk-media-comparison-grid__swap-confirmation');
        this.swapConfirmationText = this.querySelector('.bk-media-comparison-grid__swap-confirmation-text');
      }

      if (!this.swapConfirmation || !this.swapConfirmationText) return;

      const template = this.dataset.swapConfirmationMessage || 'Produkt erfolgreich gewechselt';
      const message = customMessage || template.replace(/\[\[product\]\]/g, productTitle || '');

      if (this.swapConfirmationTimeout) clearTimeout(this.swapConfirmationTimeout);

      this.swapConfirmationText.textContent = message;
      this.swapConfirmation.setAttribute('aria-hidden', 'false');
      this.swapConfirmation.classList.add('is-visible');

      this.swapConfirmationTimeout = setTimeout(() => {
        this.swapConfirmation.classList.remove('is-visible');
        this.swapConfirmation.setAttribute('aria-hidden', 'true');
      }, 3000);
    }

    onGalleryTriggerClick(event) {
      const trigger = event.target.closest('[data-comparison-gallery-trigger]');
      if (!trigger || trigger.disabled || trigger.hidden || !this.contains(trigger)) return;

      event.preventDefault();
      event.stopPropagation();

      const productId = trigger.dataset.productId;
      if (!productId) return;

      const data = this.productsData[String(productId)] || this.productsData[productId];
      if (!data?.gallery?.length) return;

      this.openProductGallery(data, trigger.querySelector('img'));
    }

    async initPhotoSwipe() {
      if (this._photoSwipeLightbox) return this._photoSwipeLightbox;

      const vendorUrl = this.dataset.vendorUrl;
      const photoswipeUrl = this.dataset.photoswipeUrl;
      if (!vendorUrl || !photoswipeUrl) return null;

      try {
        const { PhotoSwipeLightbox } = await import(vendorUrl);
        const maxZoomLevel = parseFloat(this.dataset.comparisonMaxZoom) || 3;
        const strings = window.themeVariables?.strings || {};

        const lightbox = new PhotoSwipeLightbox({
          pswpModule: () => import(photoswipeUrl),
          bgOpacity: 1,
          maxZoomLevel,
          closeTitle: strings.closeGallery || 'Close',
          zoomTitle: strings.zoomGallery || 'Zoom',
          errorMsg: strings.errorGallery || 'The image could not be loaded.',
          arrowPrev: false,
          arrowNext: false,
          counter: false,
          zoom: false
        });

        lightbox.init();
        this._photoSwipeLightbox = lightbox;
        return lightbox;
      } catch (error) {
        console.error('Failed to initialize comparison grid gallery:', error);
        return null;
      }
    }

    async openProductGallery(data, thumbnailElement) {
      const lightbox = await this.initPhotoSwipe();
      if (!lightbox) return;

      let startIndex = 0;

      if (data.imageMediaId) {
        const matchedIndex = data.gallery.findIndex((item) => String(item.id) === String(data.imageMediaId));
        if (matchedIndex >= 0) startIndex = matchedIndex;
      }

      const dataSource = data.gallery.map((item, index) => ({
        thumbnailElement: index === startIndex ? thumbnailElement : null,
        src: item.src,
        msrc: item.src,
        width: parseInt(item.width, 10) || 1200,
        height: parseInt(item.height, 10) || 1200,
        alt: item.alt || data.title || '',
        thumbCropped: true
      }));

      lightbox.loadAndOpen(startIndex, dataSource);
    }

    getSelectedComparisonProducts() {
      return this.getActiveColumnHeaders()
        .map((header) => {
          const productId = header.dataset.productId;
          return this.productsData[String(productId)] || this.productsData[productId];
        })
        .filter((data) => data?.variantId);
    }

    loadBundleDiscountTiers() {
      const script = this.querySelector('[data-comparison-bundle-discount-tiers]');
      if (!script) {
        return [
          { quantity: 2, discount: 10 },
          { quantity: 3, discount: 15 },
          { quantity: 4, discount: 20 },
          { quantity: 6, discount: 30 },
          { quantity: 8, discount: 40 }
        ];
      }

      try {
        const tiers = JSON.parse(script.textContent);
        return Array.isArray(tiers) ? tiers : [];
      } catch (error) {
        return [];
      }
    }

    getBundleDiscountPercent(count) {
      const tiers = this.loadBundleDiscountTiers().slice().sort((a, b) => a.quantity - b.quantity);
      let discount = 0;
      tiers.forEach((tier) => {
        if (count >= tier.quantity) discount = tier.discount;
      });
      return discount;
    }

    getBundleCtaText(count) {
      const base = this.dataset.bundleCtaText || 'Als Set kaufen';
      const quantity = Number.isFinite(count) ? count : this.getActiveColumnCount();
      return `${base} (${quantity})`;
    }

    formatBundleMoney(cents) {
      if (typeof window.formatMoney === 'function') return window.formatMoney(cents);
      return `€${(cents / 100).toFixed(2).replace('.', ',')}`;
    }

    getBundleReferenceUnitPrice(priceCents, compareAtPriceCents) {
      const price = parseInt(priceCents, 10) || 0;
      const compareAt = parseInt(compareAtPriceCents, 10) || 0;
      if (this.dataset.showVariantCompareAt === 'true' && compareAt > price) return compareAt;
      return price;
    }

    getBundleTotals(products) {
      let priceSubtotal = 0;
      let compareSubtotal = 0;
      const goalPercent = this.getBundleDiscountPercent(products.length);

      products.forEach((product) => {
        const price = parseInt(product.priceCents, 10) || 0;
        const compareAt = parseInt(product.compareAtPriceCents, 10) || 0;
        priceSubtotal += price;
        compareSubtotal += this.getBundleReferenceUnitPrice(price, compareAt);
      });

      const discountAmount = goalPercent > 0 ? Math.floor((priceSubtotal * Math.round(goalPercent * 100)) / 10000) : 0;
      const discountedTotal = priceSubtotal - discountAmount;

      return {
        priceSubtotal,
        compareSubtotal,
        originalTotal: priceSubtotal,
        discountedTotal,
        savings: (this.dataset.showVariantCompareAt === 'true' && compareSubtotal > priceSubtotal)
          ? compareSubtotal - discountedTotal
          : priceSubtotal - discountedTotal,
        goalPercent
      };
    }

    updateBundleButton() {
      if (!this.bundleButtons.length) return;

      const products = this.getSelectedComparisonProducts().filter((product) => product.available && product.variantId);
      const totals = this.getBundleTotals(products);
      const selectedCount = this.getActiveColumnCount();
      const isDisabled = products.length < 2;
      const savingsText = totals.savings > 0 ? `-${this.formatBundleMoney(totals.savings)}` : '';
      const showSavings = products.length > 0 && totals.savings > 0;
      const showPrices = products.length > 0;
      const ctaText = this.getBundleCtaText(selectedCount);

      this.bundleButtons.forEach((button) => {
        const textTarget = button.querySelector('[data-comparison-bundle-action-text], [data-comparison-bundle-action-text-mobile]');
        if (textTarget) textTarget.textContent = ctaText;
        button.disabled = isDisabled;
        button.toggleAttribute('disabled', isDisabled);
      });

      this.querySelectorAll('[data-comparison-bundle-prices]').forEach((pricesEl) => {
        pricesEl.hidden = !showPrices;

        const saleEl = pricesEl.querySelector('[data-comparison-bundle-sale-price]');
        const compareEl = pricesEl.querySelector('[data-comparison-bundle-compare-price]');

        if (saleEl && showPrices) {
          saleEl.textContent = this.formatBundleMoney(totals.discountedTotal);
        }

        if (compareEl && showPrices) {
          if (totals.goalPercent > 0 && totals.discountedTotal < totals.originalTotal) {
            compareEl.textContent = this.formatBundleMoney(totals.originalTotal);
            compareEl.hidden = false;
          } else {
            compareEl.textContent = '';
            compareEl.hidden = true;
          }
        }
      });

      this.bundleSavingsRows.forEach((row) => {
        row.hidden = !showSavings && !showPrices;
      });

      if (this.bundleSavingsAmountDesktop) {
        this.bundleSavingsAmountDesktop.textContent = savingsText;
        this.bundleSavingsAmountDesktop.closest('.badge')?.toggleAttribute('hidden', !showSavings);
      }

      if (this.bundleSavingsAmountMobile) {
        this.bundleSavingsAmountMobile.textContent = savingsText;
        this.bundleSavingsAmountMobile.closest('.badge')?.toggleAttribute('hidden', !showSavings);
      }
    }

    getBundleBuilderPageUrl() {
      let configuredUrl = (this.dataset.bundleBuilderPageUrl || '/pages/bundle-builder').trim();
      if (!configuredUrl) return '/pages/bundle-builder';

      if (configuredUrl.includes('shopify://pages/')) {
        const handle = configuredUrl.split('shopify://pages/')[1]?.split('?')[0]?.split('/')[0];
        return handle ? `/pages/${handle}` : '/pages/bundle-builder';
      }

      try {
        const parsed = new URL(configuredUrl, window.location.origin);
        return `${parsed.pathname}${parsed.search}`;
      } catch (error) {
        return configuredUrl.startsWith('/') ? configuredUrl : `/${configuredUrl}`;
      }
    }

    findSparsetBuilder() {
      const sectionId = (this.dataset.sparsetBuilderSectionId || '').trim();

      if (sectionId) {
        const section = document.getElementById(sectionId);
        const builderInSection = section?.querySelector('bk-media-sparset-builder');
        if (builderInSection) return builderInSection;
      }

      return document.querySelector('bk-media-sparset-builder');
    }

    generateBundleId() {
      let instanceId;
      if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        instanceId = crypto.randomUUID().replace(/-/g, '');
      } else {
        instanceId = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
      }
      return `starter-${instanceId}`;
    }

    buildBundleComponentsProperty(products) {
      const quantities = {};
      products.forEach((product) => {
        const id = String(product.variantId);
        quantities[id] = (quantities[id] || 0) + 1;
      });

      return Object.keys(quantities).map((id) => {
        const product = products.find((item) => String(item.variantId) === id);
        const title = encodeURIComponent(product?.title || '');
        const image = product?.imageUrl ? encodeURIComponent(product.imageUrl) : '';
        return image ? `${id}::${quantities[id]}::${title}::${image}` : `${id}::${quantities[id]}::${title}`;
      }).join('||');
    }

    buildBundleLineProperties(bundleId, parentVariantId, bundleTitle, totals, bundleComponents, discountPercent) {
      const priceSubtotal = totals.priceSubtotal || totals.originalTotal || 0;
      const compareSubtotal = totals.compareSubtotal || priceSubtotal;
      const discountedTotal = totals.discountedTotal || 0;

      return {
        _BundleId: bundleId,
        _BundleIsParent: 'true',
        '_Bundle role': 'parent',
        _BundleParentVariantId: String(parentVariantId),
        _BundleTitle: bundleTitle || 'Dein Wunschset',
        _BundleCompareTotal: String(priceSubtotal),
        _BundleCompareAtTotal: String(compareSubtotal),
        _BundleDiscountedTotal: String(discountedTotal),
        _BundleShowVariantCompareAt: this.dataset.showVariantCompareAt === 'true' ? 'true' : 'false',
        '_Bundle pricing': [
          priceSubtotal,
          discountedTotal,
          discountPercent || 0,
          compareSubtotal
        ].join('|'),
        _BundleComponents: bundleComponents,
        _BundleDiscountPercent: String(discountPercent || 0)
      };
    }

    async addSelectedBundleToCart(button) {
      const products = this.getSelectedComparisonProducts().filter((product) => product.available && product.variantId);
      if (products.length < this.getMinSelectable()) return;

      const parentVariantId = parseInt(this.dataset.bundleParentVariantId, 10);
      if (!parentVariantId) {
        console.error('Bundle parent product is missing for comparison set add-to-cart.');
        return;
      }

      const variantIds = products.map((product) => String(product.variantId));
      const builder = this.findSparsetBuilder();
      if (builder && typeof builder.bkMediaAddVariantsToCart === 'function') {
        await builder.bkMediaAddVariantsToCart(variantIds, { replace: true, scroll: false, button });
        return;
      }

      if (button) {
        button.setAttribute('aria-busy', 'true');
        button.disabled = true;
      }

      try {
        const totals = this.getBundleTotals(products);
        const discountPercent = totals.goalPercent || this.getBundleDiscountPercent(products.length);
        const bundleId = this.generateBundleId();
        const bundleTitle = this.dataset.bundleTitle || 'Dein Wunschset';
        const bundleComponents = this.buildBundleComponentsProperty(products);
        const parentLineProps = this.buildBundleLineProperties(
          bundleId,
          parentVariantId,
          bundleTitle,
          totals,
          bundleComponents,
          discountPercent
        );
        const childLineProps = {
          _BundleId: bundleId,
          '_Bundle role': 'child',
          _BundleParentVariantId: String(parentVariantId)
        };

        const itemQuantities = {};
        products.forEach((product) => {
          const id = parseInt(product.variantId, 10);
          itemQuantities[id] = (itemQuantities[id] || 0) + 1;
        });

        const items = [{ id: parentVariantId, quantity: 1, properties: parentLineProps }];
        Object.keys(itemQuantities).forEach((id) => {
          const variantId = parseInt(id, 10);
          if (variantId === parentVariantId) return;
          items.push({
            id: variantId,
            quantity: itemQuantities[id],
            properties: childLineProps
          });
        });

        const cartDrawerElement = document.querySelector('cart-drawer');
        const sectionId = cartDrawerElement?.getAttribute('data-section-id') || 'cart-drawer';
        let sectionsToBundle = ['variant-added'];
        document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
          bubbles: true,
          detail: { sections: sectionsToBundle }
        }));
        if (!sectionsToBundle.includes(sectionId)) {
          sectionsToBundle.push(sectionId);
        }

        const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            items,
            sections: sectionsToBundle.join(',')
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.description || 'Failed to add items to cart');
        }

        const responseJson = await response.json();
        const cartContent = await (await fetch(`${window.Shopify.routes.root}cart.js`)).json();

        if (!responseJson.sections || !responseJson.sections[sectionId]) {
          const cartDrawerResponse = await fetch(`${window.Shopify.routes.root}cart?section_id=${sectionId}`);
          const cartDrawerHtml = await cartDrawerResponse.text();
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = cartDrawerHtml;
          const newCartDrawer = tempDiv.querySelector('#cart-drawer, cart-drawer');
          if (newCartDrawer) {
            if (!responseJson.sections) responseJson.sections = {};
            responseJson.sections[sectionId] = newCartDrawer.outerHTML;
          }
        }

        if (!cartContent.sections) cartContent.sections = {};
        cartContent.sections = Object.assign(cartContent.sections, responseJson.sections || {});

        const productForm = document.querySelector('form[is="product-form"]');
        const formToUse = productForm || document.createElement('form');
        let formAdded = false;
        if (!productForm) {
          document.body.appendChild(formToUse);
          formAdded = true;
        }

        formToUse.dispatchEvent(new CustomEvent('variant:add', {
          bubbles: true,
          cancelable: true,
          detail: {
            items: responseJson.items || [responseJson],
            cart: cartContent
          }
        }));

        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: {
            baseEvent: 'variant:add',
            cart: cartContent
          }
        }));

        if (formAdded) {
          document.body.removeChild(formToUse);
        }
      } catch (error) {
        console.error('Error adding comparison set to cart:', error);
        alert(error.message || 'Produkte konnten nicht hinzugefügt werden.');
      } finally {
        if (button) {
          button.removeAttribute('aria-busy');
          const productsAfter = this.getSelectedComparisonProducts().filter((product) => product.available && product.variantId);
          button.disabled = productsAfter.length < this.getMinSelectable();
          button.toggleAttribute('disabled', button.disabled);
        }
      }
    }

    async onBundleClick(event) {
      event.preventDefault();
      const button = event.currentTarget;
      await this.addSelectedBundleToCart(button);
    }

    escapeHtml(value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }
  }

  customElements.define('bk-media-product-comparison-grid', BkMediaProductComparisonGrid);
}
